import React, { useEffect, useState } from 'react';
import {
  getAuth,
  updateProfile,
  updatePassword,
  sendEmailVerification,
  deleteUser,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth';
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import './Profile.css';
import Emoji from "./Emoji";


const Profile = () => {
  const auth = getAuth();
  const user = auth.currentUser;
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [newPassword, setNewPassword] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [emailVerified, setEmailVerified] = useState(user?.emailVerified || false);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true); 

useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;

      await user.reload(); // Force refresh
      setEmailVerified(user.emailVerified);
      setDisplayName(user.displayName || '');

      // ✅ Update Firestore field if verified
      if (user.emailVerified) {
        try {
          await updateDoc(doc(db, 'users', user.uid), {
            emailVerified: true,
          });
        } catch (err) {
          console.warn("⚠️ Couldn't update Firestore verification field:", err);
        }
      }

      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists()) {
          const data = snap.data();
          setUserRole(data.role || '');
        }
      } catch (err) {
        console.error("❌ Failed to load user doc:", err);
      }

      setLoading(false);
    };

    fetchUserData();
  }, [user]);

  if (loading) return <div style={{ padding: 30 }}>Loading your profile...</div>;

  const handleUpdateName = async () => {
    try {
      await updateProfile(user, { displayName });
      await updateDoc(doc(db, 'users', user.uid), {
        name: displayName,
      });
      setStatusMsg('✅ Name updated successfully!');
    } catch (err) {
      setStatusMsg('❌ Failed to update name.');
      console.error(err);
    }
  };

  const handleUpdatePassword = async () => {
    try {
      if (newPassword.length < 6) {
        setStatusMsg('❗ Password must be at least 6 characters.');
        return;
      }

      const currentUser = auth.currentUser;
      const email = currentUser?.email;
      if (!email) {
        setStatusMsg('❌ Missing user email.');
        return;
      }

      const confirmedPassword = prompt('Please confirm your current password to change it:');
      if (!confirmedPassword) {
        setStatusMsg('❌ Password confirmation canceled.');
        return;
      }

      const credential = EmailAuthProvider.credential(email, confirmedPassword);
      await reauthenticateWithCredential(currentUser, credential);

      await updatePassword(currentUser, newPassword);
      setStatusMsg('✅ Password updated successfully!');
      setNewPassword('');
    } catch (err) {
      setStatusMsg('❌ Failed to update password. Try again or log in again.');
      console.error(err);
    }
  };

  const handleResendVerification = async () => {
    try {
      await sendEmailVerification(user);
      setStatusMsg('📨 Verification email sent!');
    } catch (err) {
      setStatusMsg('❌ Failed to send verification email.');
      console.error(err);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm('⚠️ Are you sure you want to permanently delete your account?');
    if (!confirmed) return;

    const password = prompt('Please enter your password to confirm:');
    if (!password) return;

    try {
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);

      const subPaths = ['writings', 'notifications', 'mockAnswers', 'mockScores'];
      for (let path of subPaths) {
        const colRef = collection(db, 'users', user.uid, path);
        const snaps = await getDocs(colRef);
        for (let docSnap of snaps.docs) {
          await deleteDoc(docSnap.ref);
        }
      }

      await deleteDoc(doc(db, 'users', user.uid));
      await deleteUser(user);

      alert('✅ Account deleted successfully.');
      window.location.href = '/signin';
    } catch (error) {
      console.error('❌ Account deletion failed:', error);
      alert('Failed to delete account. Check your password or try again.');
    }
  };

  // ⏳ Loading State UI
  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-card">
          <h2>Loading your profile...</h2>
          <div className="spinner" style={{ marginTop: '20px' }}>
            <div className="lds-dual-ring"></div>
          </div>
        </div>
      </div>
    );
  }
const handleCheckEmailVerified = async () => {
  try {
    await user.reload();
    const refreshedUser = auth.currentUser;

    if (refreshedUser.emailVerified) {
      setEmailVerified(true);
      setStatusMsg('✅ Email verified successfully!');

      try {
        await updateDoc(doc(db, 'users', user.uid), {
          emailVerified: true,
        });
      } catch (err) {
        console.warn("⚠️ Couldn't update Firestore verification field:", err);
      }
    } else {
      setStatusMsg('⚠️ Email is still not verified. Please check your inbox and spam folder.');
    }
  } catch (err) {
    console.error('❌ Error checking email verification:', err);
    setStatusMsg('❌ Failed to check email status.');
  }
};

  return (
    <div className="profile-container">
      <div className="profile-card">
        <Emoji symbol="👤" label="user profile" /><h2>Profile Settings</h2>

        <p><strong>Email:</strong> {user?.email}</p>

        {!emailVerified && (
          <div className="warning-box">
            <Emoji symbol="⚠️" label="warning" /> Your email is not verified yet. Please check your inbox.
            <br />
            <strong>Once verified, refresh this page to unlock full access.</strong>
          </div>
        )}

        <p>
  <strong>Email Verified:</strong>{' '}
  {emailVerified ? '✅ Yes' : '❌ No'}
  {!emailVerified && (
    <>
      <button className="btn small" onClick={handleResendVerification}>
        Resend Verification
      </button>
      <button className="btn small" style={{ marginLeft: '10px' }} onClick={handleCheckEmailVerified}>
        <Emoji symbol="🔄" label="refresh" /> I've verified my email
      </button>
    </>
  )}
</p>


        <p><strong>Role:</strong> {userRole}</p>
        <hr />

        <label>Display Name:</label>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <button className="btn primary" onClick={handleUpdateName}>Update Name</button>

        <hr />

        <label>New Password:</label>
        <input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <button className="btn primary" onClick={handleUpdatePassword}>Update Password</button>

        {statusMsg && <p className="status-msg">{statusMsg}</p>}

        <button className="btn secondary" onClick={() => navigate(-1)}>
          <Emoji symbol="🔙" label="back arrow" /> To Dashboard
        </button>

        <a
          href="https://billing.stripe.com/p/login/test_4gM3cvclKfUH8A7eNBaEE00"
          target="_blank"
          rel="noopener noreferrer"
          className="btn manage"
        >
          Manage my Subscription
        </a>

        <hr />

        <button className="btn danger" onClick={handleDeleteAccount}>Delete My Account</button>
        <p className="delete-note">
          Deleting your Account doesn't cancel your subscription. Please cancel it first above in "Manage Your Subscription" or email info@beckettacademia.com.
        </p>
      </div>
    </div>
  );
};

export default Profile;
